import { ComponentType, ElementType, FC, ReactNode } from 'react';
import { Role } from 'utils/helpers/auth0.helpers';
import { useAuth0User } from 'hooks/useAuth0User';
import { FeatureFlag, featureFlagIsTrue } from 'utils/helpers/featureFlag.helpers';
import { userHasPermissions } from 'utils/helpers/permissions.helpers';

export interface RestrictedComponentProps {
  allowedRoles: Role[];
  featureFlag?: FeatureFlag;
  children: ReactNode;
}

const RestrictedComponent: FC<RestrictedComponentProps> = ({ allowedRoles, featureFlag, children }) => {
  const user = useAuth0User();
  const hasPermission = userHasPermissions(user, allowedRoles);

  const canAccess = featureFlagIsTrue(featureFlag, user) && hasPermission;

  if (!canAccess) {
    return null;
  }

  return <>{children}</>;
};

export const withRestricted =
  <P extends Record<string, any>>(
    Component: ComponentType<P> | ElementType,
    allowedRoles: Role[],
    featureFlag?: FeatureFlag,
  ): FC<P> =>
  (props: P) => {
    const user = useAuth0User();
    const hasPermission = userHasPermissions(user, allowedRoles);

    const canAccess = featureFlagIsTrue(featureFlag, user) && hasPermission;

    if (!canAccess) {
      return null;
    }

    return <Component {...props} />;
  };

export default RestrictedComponent;
